import { z } from 'zod';
import { EntityNode } from './entities.js';
import { GraphEdge } from './graph.js';
/**
 * Graph-grouping configuration. Lives in Firestore at
 * `systemConfig/graph` — admin-only write, reader-visible read.
 * The graph overview mode reads `hubCount` to decide how many
 * hub-islands to render; the admin settings page writes it.
 *
 * `hubSelection` is reserved for future extension — right now the
 * server always ranks hubs by degree centrality; "betweenness" and
 * "curated" are planned but not implemented, hence the enum is
 * defined here but the server ignores anything other than "degree".
 */
export const GraphGroupingConfig = z.object({
    /** How many hub-island clusters to render in overview mode. */
    hubCount: z.number().int().min(3).max(30).default(8),
    /** K-hop depth for each hub's neighborhood. 1 = direct neighbors only. */
    neighborhoodDepth: z.number().int().min(1).max(2).default(1),
    /** Reserved: how hubs are picked. Only "degree" is honored today. */
    hubSelection: z.enum(['degree', 'betweenness', 'curated']).default('degree'),
    /** Hand-curated hub list, used when `hubSelection === 'curated'`. */
    curatedHubs: z.array(z.string()).default([]),
    /** Stamp for admin UI — "last updated by X at Y". */
    updatedBy: z.string().optional(),
    updatedAt: z.string().optional(),
});
/**
 * One hub plus its immediate neighborhood. Returned by
 * `GET /api/graph/hubs?count=N`, one per hub, sorted by rank.
 * Rendered as an island in the graph overview.
 */
export const HubIsland = z.object({
    /** The central entity — rank 0 is the most-connected. */
    hub: EntityNode,
    /** Degree count (in + out edges, non-MENTIONS). Informs visual weight. */
    degree: z.number().int().nonnegative(),
    /** Nodes in this hub's k-hop neighborhood (excludes the hub itself). */
    neighbors: z.array(EntityNode),
    /** Edges within the island — both hub-to-neighbor and neighbor-to-neighbor. */
    edges: z.array(GraphEdge),
});
export const HubIslandsResponse = z.object({
    islands: z.array(HubIsland),
    /** Nodes appearing in the overall graph but NOT in any island.
     *  Rendered as a small "unclustered" overflow, or dropped from
     *  the overview — UI decides. */
    orphans: z.array(EntityNode).default([]),
    /** Inter-island edges — connections between entities in different
     *  hub islands. Rendered as thin lines in the overview to show
     *  cross-cluster bridges without crowding each island. */
    bridgeEdges: z.array(GraphEdge).default([]),
    /** Counts — useful for the Admin "system health" section. */
    totalEntities: z.number().int().nonnegative(),
    totalRelationships: z.number().int().nonnegative(),
});
